import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export enum UserInterfaceStateServiceLocation {
  Session = 0,
  LocalStorage = 1,
  SessionStorage = 2,
}
@Injectable({
  providedIn: 'root',
})
export class UserInterfaceStateService {
  private values: Record<string, unknown> = {};

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  private isEnabled() {
    return isPlatformBrowser(this.platformId);
  }

  getValue(key: string, defaultValue?: unknown): unknown {
    if (!this.isEnabled()) {
      return defaultValue;
    }

    if (
      this.values[key] === undefined &&
      sessionStorage.getItem(key) === null &&
      localStorage.getItem(key) === null
    ) {
      this.values[key] = defaultValue;
      return this.values[key];
    }
    if (sessionStorage.getItem(key) != null) {
      this.values[key] = sessionStorage.getItem(key);
      return this.values[key];
    }
    if (localStorage.getItem(key) != null) {
      this.values[key] = localStorage.getItem(key);
      return this.values[key];
    }

    return this.values[key] === undefined ? defaultValue : this.values[key];
  }

  setValue(key: string, value: unknown, location?: UserInterfaceStateServiceLocation): void {
    if (!this.isEnabled()) {
      return;
    }

    switch (location) {
      case UserInterfaceStateServiceLocation.LocalStorage:
        localStorage.setItem(key, String(value));
        break;

      case UserInterfaceStateServiceLocation.SessionStorage:
        sessionStorage.setItem(key, String(value));
        break;

      case UserInterfaceStateServiceLocation.Session:
      default:
        this.values[key] = value;
        break;
    }
  }
  getObject(key: string, defaultValue?: unknown): unknown {
    if (
      this.values[key] === undefined &&
      sessionStorage.getItem(key) === null &&
      localStorage.getItem(key) === null
    ) {
      this.values[key] = defaultValue;
      return this.parseValue(key, this.values[key]);
    }
    if (sessionStorage.getItem(key) != null) {
      this.values[key] = sessionStorage.getItem(key);
      return this.parseValue(key, this.values[key]);
    }
    if (localStorage.getItem(key) != null) {
      this.values[key] = localStorage.getItem(key);
      return this.parseValue(key, this.values[key]);
    }

    return this.values[key] === undefined ? defaultValue : this.parseValue(key, this.values[key]);
  }

  setObject(key: string, value: unknown, location?: UserInterfaceStateServiceLocation): void {
    const stringifyValue = JSON.stringify(value);
    switch (location) {
      case UserInterfaceStateServiceLocation.LocalStorage:
        localStorage.setItem(key, stringifyValue);
        break;

      case UserInterfaceStateServiceLocation.SessionStorage:
        sessionStorage.setItem(key, stringifyValue);
        break;

      case UserInterfaceStateServiceLocation.Session:
      default:
        this.values[key] = stringifyValue;
        break;
    }
  }
  parseValue(key: string, value: unknown): unknown {
    if (value) {
      try {
        return JSON.parse(value as string);
      } catch {
        this.clearValue(key);
        return false;
      }
    }
    return false;
  }
  clearValue(key: string): void {
    if (!this.isEnabled()) {
      return;
    }

    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
    this.values[key] = null;
  }
}
